import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import Slider from "react-slick";
import { useCart } from "../../../contextApi/cartContext";
import { useLanguage } from "../../../contextApi/languageContext";
import { useGlobalData } from "../../../contextApi/globalContextData";

import useServiceData from "../../../utility/useServiceData.js";
// Import components
import ShareButtons from "../../../utility/ShareButtons";
import FAQAccordion from "../../../utility/FAQAccordion";
import QuickNavigation from "../../../utility/serviceTemplateutility/QuickNavigation";
import CardDesign from "../../../utility/serviceTemplateutility/CardDesign";
import ListDesign from "../../../utility/serviceTemplateutility/ListDesign.jsx";
import WhyUs from "../../../utility/serviceTemplateutility/WhyUs";
import {
  CustomNextArrow,
  CustomPrevArrow,
} from "../../../utility/CustomArrow";
import MultiStepForm from "../../../utility/serviceTemplateutility/MultiStepForm";
import Testimonials from "../../homepage/Testimonials";
import OurTeam from "../../homepage/OurTeam";
import { dentalImplants } from "../../../text-files/dental/DentalImplantsData.ts";

const DentalImplants = () => {
  const { language } = useLanguage();
  const { addToCart } = useCart();
  const { sectionsData } = useGlobalData();
  const serviceUrl = typeof window !== "undefined" ? window.location.href : "";

  const content = dentalImplants[language];
  const shared = dentalImplants.shared;

  const fadeIn = (direction = "up", delay = 0) => {
    const variants = {
      hidden: {
        opacity: 0,
        x: direction === "left" ? -50 : direction === "right" ? 50 : 0,
        y: direction === "up" ? 50 : direction === "down" ? -50 : 0,
      },
      visible: {
        opacity: 1,
        x: 0,
        y: 0,
        transition: {
          duration: 0.6,
          delay,
        },
      },
    };
    return variants;
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    autoplay: true,
    speed: 500,
    autoplaySpeed: 3500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 640, settings: { slidesToShow: 1 } },
    ],
  };

  return (
    <>
      <title>Dental Implants in Dubai | Same Day Dental Implants Clinic</title>
      <meta
        name="description"
        content="Get the best dental implants in Dubai with our same day dental implants service. Restore your smile with advanced implant solutions and expert care at an affordable price. Book your FREE consultation today!"
      />
      <meta
        name="robots"
        content="follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large"
      />
      <link
        rel="canonical"
        href="https://elitebodyhome.com/services/dental-implants-in-dubai/"
      />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Dental Implants in Dubai | Same Day Dental Implants Clinic"
      />
      <meta
        property="og:description"
        content="Get the best dental implants in Dubai with our same day dental implants service. Restore your smile with advanced implant solutions and expert care at an affordable price. Book your FREE consultation today!"
      />
      <meta
        property="og:url"
        content="https://elitebodyhome.com/dental-implants-in-dubai/"
      />
      <meta property="og:site_name" content="elitebodyhome.com" />
      <meta property="og:updated_time" content="2025-01-17T14:43:59+00:00" />
      <meta
        property="og:image"
        content="https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/Fine-Lines-treatemdd.jpg"
      />
      <meta
        property="og:image:secure_url"
        content="https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/Fine-Lines-treatemdd.jpg"
      />
      <meta property="og:image:width" content="1254" />
      <meta property="og:image:height" content="700" />
      <meta property="og:image:alt" content="Dental Implants in Dubai" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="Dental Implants in Dubai | Same Day Dental Implants Clinic"
      />
      <meta
        name="twitter:description"
        content="Get the best dental implants in Dubai with our same day dental implants service. Restore your smile with advanced implant solutions and expert care at an affordable price. Book your FREE consultation today!"
      />
      <meta
        name="twitter:image"
        content="https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/Fine-Lines-treatemdd.jpg"
      />

      <div
        className="min-vh-100 font-sans text-dark mx-3"
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        {/* Hero Image Section */}
        {shared.images.main && (
          <motion.section
            className="overflow-hidden"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="container-fluid px-0 mb-4">
              <img
                src={shared.images.main}
                alt="karisma skin booster dubai"
                className="img-fluid w-100 rounded"
                style={{
                  height: "500px",
                  objectFit: "cover",
                  objectPosition: "top",
                  border: "2px solid #c69933",
                }}
              />
            </div>
          </motion.section>
        )}
        <div className="d-flex gap-4  w-100 ">
          <div
            className=" rounded-4 p-1 bg-white flex-grow-1 "
            style={{
              width: "65%",
              border: "2px solid #c69933",
            }}
          >
            {/* Breadcrumbs */}
            <nav aria-label="breadcrumb" className="p-2">
              <ol className="breadcrumb">
                {content.breadcrumbs.map((item, index) => (
                  <li
                    key={index}
                    className={`breadcrumb-item ${index === content.breadcrumbs.length - 1 ? "active" : ""
                      }`}
                    aria-current={
                      index === content.breadcrumbs.length - 1
                        ? "page"
                        : undefined
                    }
                  >
                    {index === content.breadcrumbs.length - 1 ? (
                      item
                    ) : (
                      <a href={index === 0 ? "/home" : "#"}>{item}</a>
                    )}
                  </li>
                ))}
              </ol>
            </nav>

            {/* Title Section */}
            <div className="text-center my-2">
              <motion.h1
                className="display-4 fw-bold"
                style={{ color: "#c69933" }}
                variants={fadeInUp}
              >
                {content.hero.title}
              </motion.h1>

              {content.hero.subtitle && (
                <motion.h2 className="fw-semibold mt-3" variants={fadeInUp}>
                  {content.hero.subtitle}
                </motion.h2>
              )}

              {content.hero.description && (
                <motion.p className="lead mt-3" variants={fadeInUp}>
                  {content.hero.description}
                </motion.p>
              )}
            </div>

            <QuickNavigation />

            {/* How It Works Section */}
            {content.howItWorks.title && (
              <motion.section className="my-5" variants={fadeInUp}>
                <h2 className="h2 fw-bold mb-4 text-center">
                  {content.howItWorks.title}
                </h2>
                {content.howItWorks.description && (
                  <p className="text-muted text-center">
                    {content.howItWorks.description}
                  </p>
                )}
              </motion.section>
            )}

            {/* Introduction Image */}
            {shared.images.introductionImage && (
              <motion.div className="my-5" variants={fadeInUp}>
                <div className="text-center">
                  <img
                    src={shared.images.introductionImage}
                    alt="Full Face Botox"
                    className="img-fluid rounded shadow"
                    style={{ maxHeight: "500px" }}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </motion.div>
            )}

            {/* Process Section */}
            <section id="process" className="my-5">
              <CardDesign carditems={content.procedureSteps} />
            </section>

            {/* Treatment Areas Section */}
            {(content.treatmentAreas1.length > 0 ||
              content.treatmentAreas2.length > 0) && (
                <section className="my-5">
                  <div className="text-center mb-5">
                    <motion.h2 className="fw-bold h2" variants={fadeIn()}>
                      {language === "en"
                        ? "Areas Treated with Botox Full Face"
                        : "المناطق المستهدفة في بوتوكس الوجه بالكامل"}
                    </motion.h2>
                  </div>

                  {content.treatmentAreas1.length > 0 && (
                    <motion.div
                      className="row g-4 mb-4"
                      variants={fadeIn("right")}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                    >
                      {content.treatmentAreas1.map((item, idx) => (
                        <div className="col-md-4 text-center" key={idx}>
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.title}
                              className="img-fluid rounded shadow-sm mb-3"
                              style={{ height: "200px", objectFit: "cover" }}
                            />
                          )}
                          <h5 className="fw-semibold">
                            {language === "en" ? item.title : item.ar}
                          </h5>
                        </div>
                      ))}
                    </motion.div>
                  )}

                  {content.treatmentAreas2.length > 0 && (
                    <motion.div
                      className="row g-4"
                      variants={fadeIn("left")}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                    >
                      {content.treatmentAreas2.map((item, idx) => (
                        <div className="col-md-4 text-center" key={idx}>
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.title}
                              className="img-fluid rounded shadow-sm mb-3"
                              style={{ height: "200px", objectFit: "cover" }}
                            />
                          )}
                          <h5 className="fw-semibold">
                            {language === "en" ? item.title : item.ar}
                          </h5>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </section>
              )}

            {/* Benefits Section */}
            <section id="benefits" className="my-5">
              <ListDesign listitems={content.benefits} />
            </section>

            {/* Eligibility Section */}
            <section id="eligibility" className="my-5">
              <ListDesign listitems={content.eligibility} />
            </section>

            {/* Preparation Section */}
            <section id="preparations" className="my-5">
              <ListDesign listitems={content.preparationTips} />
            </section>

            {/* Results Section */}
            <section id="results" className="my-5">
              <ListDesign listitems={content.results} />
            </section>

            {/* Before & After Section */}
            {shared.images.beforeAfter &&
              shared.images.beforeAfter.length > 0 && (
                <motion.section
                  className="my-5"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn("up")}
                >
                  <div className="text-center mb-5">
                    <h2
                      className="fw-bold h1 mb-3"
                      style={{ color: "#c69933" }}
                    >
                      {language === "en" ? "Before & After" : "قبل وبعد"}
                    </h2>
                  </div>
                  <div className="px-lg-5">
                    <Slider {...sliderSettings}>
                      {shared.images.beforeAfter.map((src, index) => (
                        <motion.div key={index} variants={fadeInUp}>
                          <div>
                            {" "}
                            {/* Add horizontal padding between slides */}
                            <img
                              src={src}
                              alt={`Slide ${index + 1}`}
                              className="img-fluid rounded-4"
                            />
                          </div>
                        </motion.div>
                      ))}
                    </Slider>
                  </div>
                </motion.section>
              )}
            {/* Aftercare Section */}
            <section className="my-5">
              <ListDesign listitems={content.aftercareTips} />
            </section>

            {/* Product Card */}
            {shared.images.main && (
              <motion.div
                className="my-5"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
              >
                <div
                  className="card border-0 shadow-lg rounded-4 overflow-hidden mx-auto"
                  style={{ maxWidth: "420px" }}
                >
                  <img
                    src={shared.images.main}
                    alt={content.meta.title}
                    className="card-img-top"
                    style={{ height: "280px", objectFit: "cover" }}
                  />
                  <div className="card-body text-center px-4 py-4">
                    <h5 className="card-title fw-bold text-uppercase text-dark mb-2">
                      {content.meta.title}
                    </h5>
                    <p className="card-text text-muted small">
                      {content.meta.description}
                    </p>
                    <h6
                      className="text-success fw-bold mt-3"
                      style={{ fontSize: "1.1rem" }}
                    >
                      {language === "en" ? "Price" : "السعر"}: AED{" "}
                      {content.meta.price}
                    </h6>
                    <button
                      className="btn rounded-pill px-4 py-2 mt-3 text-white fw-bold"
                      style={{
                        background: "#c69933",
                        border: "2px solid #c69933",
                      }}
                      onClick={() =>
                        addToCart({
                          ...shared.product,
                          image: shared.images.main,
                        })
                      }
                    >
                      {language === "en" ? "Add to Cart" : "أضف إلى السلة"}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            <WhyUs language={language} />
            <OurTeam data={sectionsData["Professional"]} />
            <Testimonials data={sectionsData["Success Stories"]} />
            <FAQAccordion faqData={content.faqData} />
            <ShareButtons url={serviceUrl} />
          </div>
          <MultiStepForm />
        </div>
      </div>
    </>
  );
};

export default DentalImplants;
